import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate, formatDateTime } from "../../api/dates";
import { formatMoney, formatQuantity } from "../../api/money";
import {
  useChangeStatus,
  useDeleteDocument,
  useDeletePhoto,
  useResolveFlag,
  useSite,
  useUploadPhoto,
  type SiteDetail,
  type SiteDocument,
} from "../../api/sites";
import Tabs, { type TabDefinition } from "../../components/Tabs";
import { AssetTab, IdentityTab, LocationTab } from "./SiteEditTabs";
import PreIssuedBoqFact from "./PreIssuedBoqFact";
import ReadinessPanel from "./ReadinessPanel";
import { flagLabel, statusTone } from "./status";

type Tab = "status" | "identity" | "asset" | "location" | "photos" | "documents" | "flags" | "history" | "readiness";

/** One site, as the drawer holds it: where it stands and where it may go
 * next, its identity and asset blocks (editable by the office; every save
 * leaves an event), its position, photos, documents, flags and history. */
export default function SiteWorkspace({ id, onClose }: { id: number; onClose?: () => void }) {
  const site = useSite(id);
  if (site.isPending) {
    return (
      <>
        <DrawerHead title="Loading site…" onClose={onClose} />
        <div className="drawer-body">
          <p className="empty-state">Loading site…</p>
        </div>
      </>
    );
  }
  if (site.isError) {
    const message =
      site.error.status === 404 ? "This site does not exist, or is not one you can see." : `Could not load this site: ${site.error.message}`;
    return (
      <>
        <DrawerHead title="Site" onClose={onClose} />
        <div className="drawer-body">
          <p className="notice bad" role="alert">
            {message}
          </p>
        </div>
      </>
    );
  }
  // Keyed by site, so moving from one site to the next starts on its Status
  // tab with its own values in the forms, never the last site's.
  return <SiteView key={site.data.id} site={site.data} onClose={onClose} />;
}

function DrawerHead({ title, sub, onClose }: { title: string; sub?: ReactNode; onClose?: () => void }) {
  return (
    <header className="drawer-head site-drawer-head">
      <div className="site-drawer-title">
        <h2>{title}</h2>
        {sub === undefined ? null : <p className="site-drawer-sub">{sub}</p>}
      </div>
      {onClose === undefined ? null : (
        <button type="button" className="row-button drawer-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      )}
    </header>
  );
}

export function SiteView({ site, onClose }: { site: SiteDetail; onClose?: () => void }) {
  // A link may ask for the Readiness tab -- the planner's "Not ready to schedule"
  // list does (`/sites/41?tab=readiness`) -- and only the office has that tab.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("tab") === "readiness" && site.editable ? "readiness" : "status");
  const open = site.flags.filter((f) => f.resolved_at === null).length;
  const tabs: TabDefinition<Tab>[] = [
    { id: "status", label: "Status" },
    { id: "identity", label: "Identity" },
    { id: "asset", label: "Asset" },
    { id: "location", label: "Location", badge: site.location.lat === null ? "none" : undefined, badgeKind: "warning" },
    { id: "photos", label: "Photos", badge: site.photos.length ? String(site.photos.length) : undefined },
    { id: "documents", label: "Documents", badge: site.documents.length ? String(site.documents.length) : undefined },
    { id: "flags", label: "Flags", badge: open ? String(open) : undefined, badgeKind: open ? "blocking" : "quiet" },
    { id: "history", label: "History" },
  ];
  // Last, so the tabs before it keep their places (the handbook's shots are numbered by them).
  if (site.editable) {
    tabs.push({ id: "readiness", label: "Readiness" });
  }
  return (
    <>
      <DrawerHead
        title={site.name}
        onClose={onClose}
        sub={
          <>
            {[site.region?.name, site.cluster?.name ?? "No cluster"].filter(Boolean).join(" · ")} · Works order{" "}
            <span className="mono">{site.identity.notif_no ?? "—"}</span>
            {site.identity.rs_no ? (
              <>
                {" · "}
                <span className="mono">{site.identity.rs_no}</span>
              </>
            ) : null}
          </>
        }
      />
      <div className="site-drawer-meta">
        <span className="status-chip">
          <span className="status-dot" style={{ "--dot": statusTone(site.status) } as CSSProperties}>
            {site.status_label}
          </span>
        </span>
        {site.priority !== null ? <span className="site-red-badge">Red List #{site.priority}</span> : null}
        <span className="site-drawer-links">
          {site.source_pdf_url ? (
            <a className="go-link" href={site.source_pdf_url} target="_blank" rel="noreferrer">
              QCP (PDF)
            </a>
          ) : null}
          <Link className="go-link" to={`/workflow/sites/${site.id}`}>
            Workflow journey
          </Link>
        </span>
      </div>
      <div className="site-drawer-tabs">
        <Tabs tabs={tabs} active={tab} onSelect={setTab} />
      </div>
      <div className="drawer-body" role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {tab === "status" ? <StatusTab site={site} /> : null}
        {tab === "identity" ? <IdentityTab site={site} /> : null}
        {tab === "asset" ? <AssetTab site={site} /> : null}
        {tab === "location" ? <LocationTab site={site} /> : null}
        {tab === "photos" ? <PhotosTab site={site} /> : null}
        {tab === "documents" ? <DocumentsTab site={site} /> : null}
        {tab === "flags" ? <FlagsTab site={site} /> : null}
        {tab === "history" ? <HistoryTab site={site} /> : null}
        {tab === "readiness" ? <ReadinessPanel siteId={site.id} /> : null}
      </div>
    </>
  );
}

function StatusTab({ site }: { site: SiteDetail }) {
  const change = useChangeStatus(site.id);
  const [note, setNote] = useState("");
  return (
    <>
      <dl className="site-facts">
        <div>
          <dt>Provider</dt>
          <dd>{site.provider?.name ?? <em className="site-unassigned">Unassigned</em>}</dd>
        </div>
        <div>
          <dt>Planned</dt>
          <dd>{site.planned_date ? formatDate(site.planned_date) : "Not planned"}</dd>
        </div>
        <div>
          <dt>Valve</dt>
          <dd>{[site.asset.valve_make, site.asset.dn_scope === null ? null : `DN${site.asset.dn_scope}`].filter(Boolean).join(", ") || "—"}</dd>
        </div>
        <div>
          <dt>Scope</dt>
          <dd>{site.asset.scope_type === "replacement" ? "Replacement" : "Maintenance"}</dd>
        </div>
        {site.red_list_date ? (
          <div>
            <dt>Red List date</dt>
            <dd>{formatDate(site.red_list_date)}</dd>
          </div>
        ) : null}
        {site.last_visit ? (
          <div className="site-facts-wide">
            <dt>Last planned visit</dt>
            <dd>
              {VISIT_LABEL[site.last_visit.outcome]} ·{" "}
              <Link to={`/routing/plans/${site.last_visit.plan_id}`}>
                {[site.last_visit.date ? formatDate(site.last_visit.date) : null, site.last_visit.plan_name, `Day ${site.last_visit.day_index + 1}`].filter(Boolean).join(" · ")}
              </Link>
              {site.last_visit.note ? ` — ${site.last_visit.note}` : ""}
            </dd>
          </div>
        ) : null}
        {site.deferred ? (
          <div className="site-facts-wide">
            <dt>Deferred</dt>
            <dd>
              {formatDate(site.deferred.at)} — {site.deferred.reason ?? ""} {site.deferred.note ?? ""}
            </dd>
          </div>
        ) : null}
        {site.boq_line ? (
          <div className="site-facts-wide">
            <dt>Priced BOQ line</dt>
            <dd>
              {site.boq_line.payment_ref} {site.boq_line.description} — {formatQuantity(site.boq_line.qty)} ×{" "}
              {formatMoney(site.boq_line.rate)} = {formatMoney(site.boq_line.line_total)}
            </dd>
          </div>
        ) : null}
        {site.pre_issued_boq !== undefined ? <PreIssuedBoqFact siteId={site.id} summary={site.pre_issued_boq} /> : null}
      </dl>
      <section className="site-move">
        <h3>Move to</h3>
        {site.allowed_next.length === 0 ? (
          <p className="muted">Nothing follows {site.status_label.toLowerCase()}.</p>
        ) : (
          <>
            <label className="field">
              Note (optional)
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="site-move-buttons">
              {site.allowed_next.map((next, index) => (
                <button
                  key={next.value}
                  type="button"
                  className={index === 0 ? "btn btn-primary" : "btn btn-secondary"}
                  disabled={change.isPending}
                  onClick={() => change.mutate({ status: next.value, note: note.trim() || null }, { onSuccess: () => setNote("") })}
                >
                  {next.label}
                </button>
              ))}
            </div>
          </>
        )}
        {change.isError ? (
          <p className="notice bad" role="alert">
            {change.error.message}
          </p>
        ) : null}
      </section>
    </>
  );
}

function PhotosTab({ site }: { site: SiteDetail }) {
  const upload = useUploadPhoto(site.id);
  const remove = useDeletePhoto(site.id);
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      {site.photos.length === 0 ? <p className="muted">No photos yet.</p> : null}
      <div className="photo-grid">
        {site.photos.map((photo) => (
          <figure key={photo.id}>
            <a href={photo.url} target="_blank" rel="noreferrer">
              <img src={photo.url} alt={photo.caption ?? photo.kind} loading="lazy" />
            </a>
            <figcaption>
              {photo.kind.replace(/_/g, " ")}
              {site.editable ? (
                <button type="button" className="go-link" onClick={() => remove.mutate(photo.id)} disabled={remove.isPending}>
                  Remove
                </button>
              ) : null}
            </figcaption>
          </figure>
        ))}
      </div>
      {site.editable ? (
        <div>
          <input
            ref={input}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                upload.mutate({ file, kind: "site_photo" });
              }
              e.target.value = "";
            }}
          />
          <button type="button" className="btn btn-secondary" onClick={() => input.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? "Uploading…" : "+ Add a photo"}
          </button>
        </div>
      ) : null}
      {upload.isError ? <p className="notice bad">{upload.error.message}</p> : null}
      {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
    </>
  );
}

/** Files kept against the site that are opened rather than shown: first, the
 * completed visit sheets filed from a route plan, each naming its visit. */
function DocumentsTab({ site }: { site: SiteDetail }) {
  const remove = useDeleteDocument(site.id);
  if (site.documents.length === 0) {
    return (
      <p className="muted">
        No documents yet. A completed visit sheet is filed from the route plan: open the stop&apos;s Note and add the sheet
        when you save it.
      </p>
    );
  }
  return (
    <>
      <ul className="site-doc-list">
        {site.documents.map((document) => (
          <li key={document.id} className="site-doc">
            <div className="site-doc-head">
              <span className="site-code-badge doc">{document.label}</span>
              <a href={document.url} target="_blank" rel="noreferrer">
                {document.filename}
              </a>
              <span className="muted">{documentSize(document.size_bytes)}</span>
            </div>
            {document.visit ? (
              <p className="site-doc-visit">
                <Link to={`/routing/plans/${document.visit.plan_id}`}>{documentVisit(document.visit)}</Link>
              </p>
            ) : null}
            <p className="muted">
              Filed {formatDateTime(document.uploaded_at)}
              {document.uploaded_by ? ` by ${document.uploaded_by}` : ""}
              {site.editable ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="go-link"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove ${document.filename} from this site?`)) {
                        remove.mutate(document.id);
                      }
                    }}
                  >
                    Remove
                  </button>
                </>
              ) : null}
            </p>
          </li>
        ))}
      </ul>
      {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
    </>
  );
}

/** "Route plan Region 1 · Day 1 · 28 Sep 2026": the visit a document records. */
function documentVisit(visit: NonNullable<SiteDocument["visit"]>): string {
  return [visit.plan_name, `Day ${visit.day_index + 1}`, visit.date ? formatDate(visit.date) : null].filter(Boolean).join(" · ");
}

function documentSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function FlagsTab({ site }: { site: SiteDetail }) {
  const resolve = useResolveFlag(site.id);
  const [notes, setNotes] = useState<Record<number, string>>({});
  if (site.flags.length === 0) {
    return <p className="muted">Nothing flagged on this site.</p>;
  }
  return (
    <>
      <ul className="site-flag-list">
        {site.flags.map((flag) => (
          <li key={flag.id} className={flag.resolved_at ? "site-flag resolved" : "site-flag"}>
            <div className="site-flag-head">
              <span className={flag.blocking ? "site-code-badge crit" : "site-code-badge warn"}>{flagLabel(flag.code)}</span>
              <span>{flag.detail}</span>
            </div>
            {flag.resolved_at ? (
              <p className="muted">
                Resolved {formatDate(flag.resolved_at)}
                {flag.resolution_note ? `: ${flag.resolution_note}` : ""}
              </p>
            ) : (
              <div className="site-resolve">
                <input
                  className="input"
                  placeholder="How it was resolved"
                  value={notes[flag.id] ?? ""}
                  onChange={(e) => setNotes({ ...notes, [flag.id]: e.target.value })}
                  aria-label={`Note for ${flag.code}`}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ flagId: flag.id, note: (notes[flag.id] ?? "").trim() || null })}
                >
                  Resolve
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {resolve.isError ? <p className="notice bad">{resolve.error.message}</p> : null}
    </>
  );
}

const VISIT_LABEL = { pending: "Pending", done: "Done", not_done: "Not done" } as const;

/** How an event reads in the history: what happened, in words. */
const EVENT_LABELS: Record<string, string> = {
  status: "Status changed",
  edited: "Edited",
  assigned: "Assigned",
  red_list: "Red List",
  cluster: "Moved cluster",
  ingested: "Created from a QCP",
  project_schedule: "Project schedule",
  deferred: "Visit deferred",
  stop_not_done: "Planned visit not done",
  document_added: "Document filed",
  document_removed: "Document removed",
  reassigned: "Handed to other field operators",
};

/** Events whose value is a route plan's id: named as the plan, not as a change. */
const PLAN_EVENTS = new Set(["stop_not_done"]);

function eventText(event: SiteDetail["events"][number]): string {
  // An edit names the block it saved; a Red List event is a rank arriving,
  // leaving or being restated by a re-upload.
  if (event.kind === "edited") {
    return `Edited the ${event.to ?? "site"}`;
  }
  if (event.kind === "red_list") {
    if (event.to === null) {
      return "Taken off the Red List";
    }
    return event.from === null ? `Put on the Red List at #${event.to}` : `Red List rank #${event.to}`;
  }
  // A document names its kind -- "Document filed: Visit sheet" -- and the file
  // name follows as the event's note.
  if (event.kind === "document_added" || event.kind === "document_removed") {
    const what = event.kind === "document_added" ? event.to : event.from;
    return what ? `${EVENT_LABELS[event.kind]}: ${what}` : EVENT_LABELS[event.kind];
  }
  if (PLAN_EVENTS.has(event.kind)) {
    return event.to ? `${EVENT_LABELS[event.kind]} (route plan #${event.to})` : EVENT_LABELS[event.kind];
  }
  const label = EVENT_LABELS[event.kind] ?? event.kind.replace(/_/g, " ");
  const change = event.from || event.to ? `${event.from ?? "—"} → ${event.to ?? "—"}` : "";
  return [label, change].filter(Boolean).join(": ");
}

function HistoryTab({ site }: { site: SiteDetail }) {
  if (site.events.length === 0) {
    return <p className="muted">Nothing has happened to this site yet.</p>;
  }
  return (
    <ol className="site-timeline">
      {site.events.map((event) => (
        <li key={event.id}>
          <span className="site-timeline-dot" aria-hidden="true" />
          <div>
            <div>{eventText(event)}</div>
            {event.note ? <div className="site-timeline-note">{event.note}</div> : null}
            <div className="muted site-timeline-when">
              {formatDateTime(event.at)} · {event.by ?? "the system"}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
